import React, { useMemo, useReducer, useState } from 'react';
import {
  Box,
  Container,
} from '@mui/material';
import { useL10n } from '@uplift/hooks';

import {
  AddressVerificationContext, addressVerificationReducer,
  EditableSectionsContext, ProviderLayout,
  ProviderLayoutJumpNav,
  ProviderLayoutPageHeader,
  LoginSettingsSection,
} from '@uplift/ui-v2';
import { useCurrentuser } from '@uplift/contexts';
import { BasicsSettingsSection } from './basics/basics.main';
import { ContactSettingsSection } from './contact/contact.main';
import { useSettingsData } from './hooks/use-settings-data.hook';
import { PayoutSettingsSection } from './payout/payout.component';

export const enum SettingsTabEnum {
  basics = 'basics',
  payout = 'payout',
  contact = 'contact',
  login = 'login',
}

const settingsTabTitles: Record<SettingsTabEnum, string> = {
  [SettingsTabEnum.basics]: 'Basics',
  [SettingsTabEnum.payout]: 'Payouts Account',
  [SettingsTabEnum.contact]: 'Contact',
  [SettingsTabEnum.login]: 'Login',
};

const sectionIds = [ SettingsTabEnum.basics, SettingsTabEnum.payout, SettingsTabEnum.contact, SettingsTabEnum.login ];

export const SettingsPage: React.FC = () => {
  const currentUser = useCurrentuser();
  const l10n = useL10n();
  const [ editRow, setEditRow ] = useState(null);
  const { data, isLoading } = useSettingsData(currentUser.id);

  const [ addressState, addressDispatch ] = useReducer(addressVerificationReducer, { shouldReverifyAddress: false, parsedVerifiedAddress: null });
  const addressCtx = useMemo(() => ({ state: addressState, dispatch: addressDispatch }), [ addressState, addressDispatch ]);

  return (
    <ProviderLayout bodyScroll={false}>
      <ProviderLayoutPageHeader
        title={l10n.pages.settings}
        jumpNav={(
          <ProviderLayoutJumpNav
            tabs={sectionIds.map(sectionId => ({ label: settingsTabTitles[sectionId], sectionId }))}
            initialNavValue={SettingsTabEnum.basics}
          />
        )}
      />
      <Container>
        <Box pt={5} pb={22} display="flex" flexDirection="column" gap="2.5rem" maxWidth="792px" mx={{ xs: -2, sm: 0 }}>

          <AddressVerificationContext.Provider value={addressCtx}>
            <EditableSectionsContext.Provider value={{ editKey: editRow, setEditKey: setEditRow }}>
              <div id={SettingsTabEnum.basics}>
                <BasicsSettingsSection userId={currentUser.id} data={data} isLoading={isLoading} />
              </div>
              <div id={SettingsTabEnum.payout}>
                <PayoutSettingsSection userId={currentUser.id} data={data} isLoading={isLoading} />
              </div>
              <div id={SettingsTabEnum.contact}>
                <ContactSettingsSection userId={currentUser.id} data={data} isLoading={isLoading} />
              </div>
              <div id={SettingsTabEnum.login}>
                <LoginSettingsSection userId={currentUser.id} />
              </div>
            </EditableSectionsContext.Provider>
          </AddressVerificationContext.Provider>

        </Box>
      </Container>
    </ProviderLayout>
  );
};
